import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Typography, CircularProgress } from '@mui/material'
import { PrintableReport } from './components/PrintableReport'
import {
  ECGAnnotationsProvider,
  useECGAnnotations
} from './components/ECG/context/ECGAnnotationsContext'
import {
  XRayAnnotationsProvider,
  useXRayAnnotations
} from './components/XRay/context/XRayAnnotationsContext'
import { DIAGNOSTICS } from 'navigation/CONSTANTS'
import ButtonBase from 'design-system/button'
import './printView.css'
import { IExamResponse } from 'types/exam/types'
import { getExamDetail } from 'services/exam'
import { getEstablishmentById } from 'services/establishment/establishmentService'

const PrintViewContent = ({
  diagnosticDetail,
  logoUrl
}: {
  diagnosticDetail: IExamResponse
  logoUrl?: string | null
}) => {
  const { annotations: ecgAnnotations } = useECGAnnotations()
  const { annotations: xrayAnnotations } = useXRayAnnotations()

  // Tentar recuperar revisão médica do sessionStorage
  const [medicalReport, setMedicalReport] = useState('')

  useEffect(() => {
    const storedReview = sessionStorage.getItem('diagnostic-review')
    if (storedReview) {
      try {
        const review = JSON.parse(storedReview)
        setMedicalReport(review.medicalReport || '')
      } catch (err) {
        console.error('Error parsing stored review:', err)
      }
    }
  }, [])

  useEffect(() => {
    // ?preview=1 permite inspecionar o layout sem abrir o diálogo de impressão
    const isPreview = new URLSearchParams(globalThis.location.search).has(
      'preview'
    )
    if (isPreview) return

    // Auto-print quando a página carregar
    // Aguarda 2 segundos para garantir que as imagens foram carregadas via proxy
    const timer = setTimeout(() => {
      globalThis.print()
    }, 2000)

    return () => clearTimeout(timer)
  }, [])

  return (
    <div className='print-view-wrapper'>
      <PrintableReport
        diagnosticDetail={diagnosticDetail}
        ecgAnnotations={ecgAnnotations}
        xrayAnnotations={xrayAnnotations}
        medicalReport={medicalReport}
        logoUrl={logoUrl}
      />
    </div>
  )
}

export const PrintView = () => {
  const navigate = useNavigate()
  const { diagnosticId } = useParams<{ diagnosticId: string }>()
  const [diagnosticData, setDiagnosticData] = useState<IExamResponse | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [logoUrl, setLogoUrl] = useState<string | null>(null)

  useEffect(() => {
    const fetchDiagnostic = async () => {
      if (!diagnosticId) {
        setError('ID da análise não fornecido')
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        const response = await getExamDetail(diagnosticId)

        if (response.success && response.data) {
          setDiagnosticData(response.data)

          // Logotipo do hospital dono do exame. Buscado antes de liberar a
          // tela porque o auto-print dispara 2s após o render — se a imagem
          // chegasse depois, sairia de fora da impressão. Falha aqui não
          // impede o laudo: o cabeçalho apenas fica sem logotipo.
          if (response.data.id_hospital) {
            const establishment = await getEstablishmentById(
              response.data.id_hospital
            )
            if (establishment.success && establishment.data) {
              setLogoUrl(establishment.data.logo_url ?? null)
            } else {
              // Sem toast (não é erro do laudo), mas o console precisa contar a
              // história — senão a ausência do logotipo fica indistinguível de
              // "este hospital não tem logotipo".
              console.warn(
                '[PrintView] Falha ao buscar o estabelecimento',
                response.data.id_hospital,
                'status:',
                establishment.status,
                establishment.errors
              )
            }
          }
        } else {
          const errorMessage =
            response.status === 404
              ? 'Análise não encontrada'
              : response.status === 400
                ? 'Requisição inválida'
                : 'Erro ao carregar dados da análise'
          setError(errorMessage)
        }
      } catch (err: any) {
        // Verificar status do erro
        if (err?.response?.status === 404) {
          setError('Análise não encontrada')
        } else if (err?.response?.status === 400) {
          setError('Requisição inválida. Verifique o ID da análise.')
        } else if (err?.response?.status === 403) {
          setError('Você não tem permissão para acessar esta análise')
        } else {
          setError('Erro ao carregar dados da análise')
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchDiagnostic()
  }, [diagnosticId])

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error || !diagnosticData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant='h5' color='error'>
          {error || 'Análise não encontrada'}
        </Typography>
        <ButtonBase onClick={() => navigate(DIAGNOSTICS)} sx={{ mt: 2 }}>
          Voltar para listagem
        </ButtonBase>
      </Box>
    )
  }

  return (
    <div className='print-view-wrapper'>
      <ECGAnnotationsProvider>
        <XRayAnnotationsProvider>
          <PrintViewContent
            diagnosticDetail={diagnosticData}
            logoUrl={logoUrl}
          />
        </XRayAnnotationsProvider>
      </ECGAnnotationsProvider>
    </div>
  )
}

export default PrintView
